import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { WritableDraft } from "immer/dist/internal";
import { PalletDetail } from "../../types/palletDetail";
import { ReduxStatePalletCache } from "../../types/redux/reduxStatePalletCache";
import moment, { Moment } from "moment";
import { maxBy } from "lodash";
import { initialStatePalletCache, initialStatePalletSearch } from "../defaultState";
import { MomentObject } from "../../types/momentObject";
import {
    createPartPallet,
    getPalletsByDepartmentDate,
    getPalletsByDepartmentDateBulk,
    getPalletsByDepartmentUpdate,
    getPalletsByDepartmentUpdateBulk,
    getPalletsWithDefects,
    updatePalletsData
} from "../../aplApi/palletsHelper";
import { closeModalRedux, setModalError } from "./modalPallets2.feature";
import { getAplError } from "../../aplApi/exceptionHelper";
import { IRootState } from "../../types/redux/IRootStore";
import { stringContains } from "../../aplUtils/stringUtills";
import { createPartPalletReduxEdit, updatePalletsDataReduxEdit } from "./palletEdit.feature";

const palletsDiffUpdate = (
    palletsBaseData: PalletDetail[] | WritableDraft<WritableDraft<PalletDetail>>[],
    dateSelectionChanged: boolean,
    departmentId: number
) => palletsBaseData && palletsBaseData.length > 0 && !dateSelectionChanged && palletsBaseData[0].departmentId === departmentId;

export const getPalletsRedux = createAsyncThunk("palletCache/getPalletsRedux", async (departmentId: number, thunkApi) => {
    try {
        let state = thunkApi.getState() as IRootState;
        let palletCache = state.palletCache;
        let palletSearch = state.palletCache.palletSearch;

        if (palletsDiffUpdate(palletCache.palletsBaseData, palletSearch.dateSelectionChanged, departmentId)) {
            // pallets update function
            let lastUpdated =
                maxBy(palletCache.palletsBaseData, (x: PalletDetail) => x.detailsUpdated)?.detailsUpdated ?? moment("2000-01-01");

            if (palletSearch.bulk) {
                // mass activties update
                return (
                    await getPalletsByDepartmentUpdateBulk(
                        departmentId,
                        palletSearch.dateStartSelectorDate,
                        palletSearch.dateEndSelectorDate,
                        palletSearch.bulkLine,
                        palletSearch.bulkWorkorder,
                        palletSearch.bulkBatch,
                        palletSearch.bulkLot,
                        palletSearch.bulkMaterial,
                        palletSearch.bulkStartRank ?? 0,
                        palletSearch.bulkEndRank ?? 0,
                        palletSearch.bulk,
                        lastUpdated,
                        palletSearch.showAllPallets,
                        palletSearch.bulkStatus,
                        palletSearch.bulkCondition
                    )
                ).data;
            } else {
                // normal activities update
                return (
                    await getPalletsByDepartmentUpdate(
                        departmentId,
                        palletSearch.dateStartSelectorDate,
                        palletSearch.dateEndSelectorDate,
                        lastUpdated,
                        palletSearch.showAllPallets
                    )
                ).data;
            }
        } else {
            // console.log(`${new Date().toISOString()} - getPalletsRedux - don't have pallets`);
            if (palletSearch.bulk) {
                // mass activties base
                return (
                    await getPalletsByDepartmentDateBulk(
                        departmentId,
                        palletSearch.dateStartSelectorDate,
                        palletSearch.dateEndSelectorDate,
                        palletSearch.bulkLine,
                        palletSearch.bulkWorkorder,
                        palletSearch.bulkBatch,
                        palletSearch.bulkLot,
                        palletSearch.bulkMaterial,
                        palletSearch.bulkStartRank ?? 0,
                        palletSearch.bulkEndRank ?? 0,
                        palletSearch.bulk,
                        palletSearch.showAllPallets,
                        palletSearch.bulkStatus,
                        palletSearch.bulkCondition
                    )
                ).data;
            } else {
                // normal activities base
                return (
                    await getPalletsByDepartmentDate(
                        departmentId,
                        palletSearch.dateStartSelectorDate,
                        palletSearch.dateEndSelectorDate,
                        palletSearch.showAllPallets
                    )
                ).data;
            }
        }
    } catch (error: any) {
        return thunkApi.rejectWithValue(getAplError(error, error.response.data));
    }
});

export const updatePalletsReduxCache = createAsyncThunk(
    "palletCache/updatePalletsReduxCache",
    async (pallets: PalletDetail[], thunkApi) => {
        if (stringContains(window.location.href, "edit/")) {
            thunkApi.dispatch(updatePalletsDataReduxEdit(pallets));
            return [];
        }
        try {
            await updatePalletsData(pallets);
            let updatedPallets = (await getPalletsWithDefects(pallets)).data;
            thunkApi.dispatch(closeModalRedux());
            return updatedPallets;
        } catch (error: any) {
            let aplError = getAplError(error, error.response.data);
            thunkApi.dispatch(setModalError(aplError));
            return thunkApi.rejectWithValue(aplError);
        }
    }
);

export const createPartPalletReduxCache = createAsyncThunk(
    "palletCache/createPartPalletReduxCache",
    async (pallets: PalletDetail[], thunkApi) => {
        if (stringContains(window.location.href, "edit/")) {
            thunkApi.dispatch(createPartPalletReduxEdit(pallets));
            return [];
        }
        try {
            await createPartPallet(pallets);
            let updatedPallets = (await getPalletsWithDefects(pallets)).data;
            thunkApi.dispatch(closeModalRedux());
            return updatedPallets;
        } catch (error: any) {
            return thunkApi.rejectWithValue(getAplError(error, error.response.data));
        }
    }
);

const mergeBaseAndUpdate = (
    start: Moment | MomentObject,
    state: ReduxStatePalletCache,
    newPallets: PalletDetail[] | WritableDraft<PalletDetail>[]
): ReduxStatePalletCache => {
    let backlogCutOff = moment(start).subtract(1, "days");

    if (newPallets.length === 0) return { ...state, palletsLoading: false, lastUpdateOrSelect: new Date() };

    if (state.palletsBaseData) {
        let cache = state.palletsBaseData ? [...state.palletsBaseData] : [];
        let baseFiltered = state.palletsBaseData.filter(
            (arr1: PalletDetail) =>
                !newPallets.map((arr2: PalletDetail) => arr2.palletId).includes(arr1.palletId) &&
                moment(arr1.detailsUpdated).isAfter(backlogCutOff)
        );
        cache = [...baseFiltered, ...newPallets];
        return {
            ...state,
            palletsLoading: false,
            palletsBaseData: cache,
            lastUpdateOrSelect: new Date()
        } as ReduxStatePalletCache;
    } else {
        return {
            ...state,
            palletsLoading: false,
            lastUpdateOrSelect: new Date(),
            palletSearch: { ...state.palletSearch, dateSelectionChanged: false }
        } as ReduxStatePalletCache;
    }
};

const palletCache = createSlice({
    name: "palletCache",
    initialState: initialStatePalletCache,
    reducers: {
        onSetSelectedPallets: (state, action): ReduxStatePalletCache => ({
            ...state,
            selectedPallets: action.payload,
            lastUpdateOrSelect: new Date()
        }),
        onclearSelectedPallets: (state): ReduxStatePalletCache => ({ ...state, selectedPallets: [], lastUpdateOrSelect: new Date() }),
        onClearCachedPallets: (state): ReduxStatePalletCache => ({ ...state, palletsBaseData: [] }),
        onSetModalShown: (state, action) => ({ ...state, modalShown: action.payload }),
        onSetGridPage: (state, action) => ({ ...state, currentGridPage: action.payload }),
        clearPalletCache: (): ReduxStatePalletCache => initialStatePalletCache,

        saveSearch: (state, action) => ({ ...state, palletSearch: { ...state.palletSearch, form: action.payload } }),
        onDtStartChange: (state, action) => ({
            ...state,
            palletSearch: {
                ...state.palletSearch,
                dateStartSelectorVisible: false,
                dateStartSelectorDate: moment(action.payload).toObject(),
                dateSelectionChanged: true
            }
        }),
        onDtEndChange: (state, action) => ({
            ...state,
            palletSearch: {
                ...state.palletSearch,
                dateEndSelectorDate: moment(action.payload).toObject(),
                dateSelectionChanged: true
            }
        }),
        onSetFilters: (state, action): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, filters: action.payload }
        }),
        onSetBulk: (state, action): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, bulk: action.payload, dateSelectionChanged: true }
        }),
        onToggleBulk: (state): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, bulk: !state.palletSearch.bulk, dateSelectionChanged: true }
        }),
        onResetDateSelectionChanged: (state): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, dateSelectionChanged: false }
        }),
        onSetBulkLine: (state, action): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, bulkLine: action.payload, dateSelectionChanged: true }
        }),
        onSetBulkWorkorder: (state, action): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, bulkWorkorder: action.payload, dateSelectionChanged: true }
        }),
        onSetBulkBatch: (state, action): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, bulkBatch: action.payload, dateSelectionChanged: true }
        }),
        onSetBulkLot: (state, action): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, bulkLot: action.payload, dateSelectionChanged: true }
        }),
        onSetBulkMaterial: (state, action) => ({
            ...state,
            palletSearch: { ...state.palletSearch, bulkMaterial: action.payload, dateSelectionChanged: true }
        }),
        onSetBulkStartRank: (state, action): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, bulkStartRank: action.payload ?? 0, dateSelectionChanged: true }
        }),
        onSetBulkEndRank: (state, action): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, bulkEndRank: action.payload ?? 0, dateSelectionChanged: true }
        }),
        onSetBulkStatus: (state, action): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, bulkStatus: action.payload, dateSelectionChanged: true }
        }),
        onSetBulkCondition: (state, action) => ({
            ...state,
            palletSearch: { ...state.palletSearch, bulkCondition: action.payload, dateSelectionChanged: true }
        }),
        onSetPage: (state, action): ReduxStatePalletCache => ({ ...state, palletSearch: { ...state.palletSearch, page: action.payload } }),
        onSetSort: (state, action): ReduxStatePalletCache => ({ ...state, palletSearch: { ...state.palletSearch, sort: action.payload } }),
        onSetQualitySwitch: (state, action): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, qualitySwitch: action.payload }
        }),
        onSetShowAllPallets: (state, action): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, showAllPallets: action.payload, dateSelectionChanged: true }
        }),
        onClearPalletSearch: (state): ReduxStatePalletCache => ({ ...state, palletSearch: initialStatePalletSearch }),
        onSetSsccSearch: (state, action): ReduxStatePalletCache => ({
            ...state,
            palletSearch: { ...state.palletSearch, ssccSearch: action.payload }
        })
    },
    extraReducers: builder => {
        builder
            .addCase(getPalletsRedux.pending, state => {
                return { ...state, palletsLoading: true, lastUpdateOrSelect: new Date() } as ReduxStatePalletCache;
            })
            .addCase(getPalletsRedux.fulfilled, (state, action) => {
                let departmentId = action.meta.arg as number;
                let diffUpdate = palletsDiffUpdate(state.palletsBaseData, state.palletSearch.dateSelectionChanged, departmentId);
                if (action.payload.length === 0 && diffUpdate) {
                    return {
                        ...state,
                        palletsLoading: false
                    };
                }

                if (!diffUpdate) {
                    // pallets base function
                    let returnObject = {
                        ...state,
                        palletsLoading: false,
                        palletsFirstLoaded: true,
                        palletsLoadErrorMessage: null,
                        palletsBaseData: action.payload,
                        lastUpdateOrSelect: new Date(),
                        palletSearch: { ...state.palletSearch, dateSelectionChanged: false },
                        selectedPallets: []
                    } as ReduxStatePalletCache;
                    return returnObject;
                } else {
                    // pallets update function
                    let returnObject = mergeBaseAndUpdate(state.palletSearch.dateStartSelectorDate, state, action.payload);
                    return { ...returnObject, palletsLoadErrorMessage: null };
                }
            })
            .addCase(getPalletsRedux.rejected, (state, action) => {
                return {
                    ...state,
                    palletsLoading: false,
                    palletsLoadErrorMessage: action.error.code === "ERR_CANCELED" ? null : action.payload,
                    lastUpdateOrSelect: new Date()
                } as ReduxStatePalletCache;
            });

        builder
            .addCase(updatePalletsReduxCache.pending, (state, action) => {
                return { ...state, palletsLoading: true, lastUpdateOrSelect: new Date() } as ReduxStatePalletCache;
            })
            .addCase(updatePalletsReduxCache.fulfilled, (state: any, action) => {
                if (action.payload.length === 0)
                    return {
                        ...state,
                        palletsLoading: false
                    };
                let returnObject = mergeBaseAndUpdate(state.palletSearch.start, state, action.payload);
                return { ...returnObject, palletsUpdateErrorMessage: null };
            })
            .addCase(updatePalletsReduxCache.rejected, (state, action) => {
                return {
                    ...state,
                    palletsLoading: false,
                    palletsUpdateErrorMessage: action.error.code === "ERR_CANCELED" ? null : action.payload,
                    lastUpdateOrSelect: new Date()
                } as ReduxStatePalletCache;
            });

        builder
            .addCase(createPartPalletReduxCache.pending, (state, action) => {
                return { ...state, palletsLoading: true, lastUpdateOrSelect: new Date() } as ReduxStatePalletCache;
            })
            .addCase(createPartPalletReduxCache.fulfilled, (state: any, action) => {
                if (action.payload.length === 0)
                    return {
                        ...state,
                        palletsLoading: false
                    };
                let returnObject = mergeBaseAndUpdate(state.palletSearch.start, state, action.payload);
                return { ...returnObject, palletsUpdateErrorMessage: null } as ReduxStatePalletCache;
            })
            .addCase(createPartPalletReduxCache.rejected, (state, action) => {
                return {
                    ...state,
                    palletsLoading: false,
                    palletsUpdateErrorMessage: action.error.code === "ERR_CANCELED" ? null : action.payload,
                    lastUpdateOrSelect: new Date()
                } as ReduxStatePalletCache;
            });
    }
});
export const {
    onSetSelectedPallets,
    onSetModalShown,
    onSetGridPage,
    onclearSelectedPallets,
    onClearCachedPallets,
    clearPalletCache,
    // imported from palletSearch
    saveSearch,
    onDtStartChange,
    onDtEndChange,
    onSetFilters,
    onSetBulk,
    onToggleBulk,
    onResetDateSelectionChanged,
    onSetBulkLine,
    onSetBulkWorkorder,
    onSetBulkBatch,
    onSetBulkLot,
    onSetBulkMaterial,
    onSetBulkStartRank,
    onSetBulkEndRank,
    onSetBulkStatus,
    onSetBulkCondition,
    onSetPage,
    onSetSort,
    onSetQualitySwitch,
    onSetShowAllPallets,
    onClearPalletSearch,
    onSetSsccSearch
} = palletCache.actions;
export default palletCache.reducer;
